import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell,
  ComposedChart, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns            as getCampaigns,
  getDashboardSentimentPolarity    as getPolarityData,
  getDashboardSentimentEmotion     as getEmotionData,
  getDashboardSentimentScore       as getScoreData,
  getDashboardSentimentWilling     as getWillingData,
} from "../../services/pbi/pbiservices";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";
const PURPLE = "#7F77DD";
const GRAY   = "#888780";
const TEAL   = "#0F6E56";
const RED    = "#A32D2D";

const POLARITY_COLORS = {
  "Very Positive": GREEN,   // #1D9E75
  "Positive":      BLUE,    // #378ADD
  "Neutral":       AMBER,   // #BA7517
  "Negative":      PINK,    // #D4537E
  "Very Negative": RED,     // #A32D2D
};

const WILLING_COLORS = {
  "Full today":     "#1D9E75",
  "Partial today":  "#378ADD",
  "Full future":    "#7F77DD",
  "Partial future": "#BA7517",
  "Not Willing":    "#D4537E",
};

const EMOTION_COLORS = [
  BLUE, PINK, GREEN, AMBER, PURPLE, TEAL, GRAY,
  "#185FA5","#0F6E56","#993556","#3C3489","#633806",
];

const SCORE_COLORS = ["#A32D2D","#E24B4A","#BA7517","#1D9E75","#0C447C"];

const GRAN_OPTIONS = ["Day", "Week", "Month"];

// ── Date range presets ─────────────────────────────────────────────
const RANGE_OPTIONS = [
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

function getDateRange(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "7D":
      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":
      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":
      start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":
      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":
      start = thisMonday; end = now; break;
    case "CW+Last":
      start = lastMonday; end = now; break;
    case "This Mo":
      start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":
      start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":
      start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:
      start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  const toLocal = (d) => {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  };
  return {
    startDate: toLocal(start),
    endDate:   toLocal(end),
  };
}

// Previous period = same duration as current, ending the day before current starts.
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start  = parse(startDate);
  const end    = parse(endDate);
  const durMs  = end.getTime() - start.getTime();
  const dayMs  = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Helpers ────────────────────────────────────────────────────────
function fmt(n, dec = 0) {
  const num = Number(n);
  if (n == null || isNaN(num)) return "—";
  return num.toFixed(dec);
}

function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  // new Date("2026-04-16") parses as UTC midnight → shifts back a day
  // in negative-offset timezones. Slashes force local-time parsing.
  const d = new Date(s.replace(/-/g, "/"));
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// Build a delta object from current/prev numeric values.
function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

// Shared formatters for non-count prev values.
const pctFmt   = v => `${fmt(v, 2)}%`;
const score2dp = v => fmt(v, 2);

// ── Aggregation helpers (shared by current & prev period) ──────────
function aggregatePolarity(rows) {
  const total    = (rows || []).reduce((a,b) => a + safeNum(b.total),         0);
  const posCount = (rows || []).reduce((a,b) =>
    a + safeNum(b.very_positive) + safeNum(b.positive), 0);
  const neuCount = (rows || []).reduce((a,b) => a + safeNum(b.neutral),       0);
  const negCount = (rows || []).reduce((a,b) =>
    a + safeNum(b.negative) + safeNum(b.very_negative), 0);
  const veryPos  = (rows || []).reduce((a,b) => a + safeNum(b.very_positive), 0);
  const veryNeg  = (rows || []).reduce((a,b) => a + safeNum(b.very_negative), 0);
  return {
    total, posCount, neuCount, negCount, veryPos, veryNeg,
    posPct:     total > 0 ? posCount / total * 100 : null,
    neuPct:     total > 0 ? neuCount / total * 100 : null,
    negPct:     total > 0 ? negCount / total * 100 : null,
    veryPosPct: total > 0 ? veryPos  / total * 100 : null,
    veryNegPct: total > 0 ? veryNeg  / total * 100 : null,
  };
}

function aggregateScore(rows) {
  const total = (rows || []).reduce((a,b) => a + safeNum(b.total), 0);
  return {
    total,
    avgScore: total > 0
      ? (rows || []).reduce((a,b) => a + safeNum(b.avg_score) * safeNum(b.total), 0) / total
      : null,
    score1: (rows || []).reduce((a,b) => a + safeNum(b.score_1), 0),
    score5: (rows || []).reduce((a,b) => a + safeNum(b.score_5), 0),
  };
}

function aggregateWilling(rows) {
  const total        = (rows || []).reduce((a,b) => a + safeNum(b.total),          0);
  const fullToday    = (rows || []).reduce((a,b) => a + safeNum(b.full_today),     0);
  const partialToday = (rows || []).reduce((a,b) => a + safeNum(b.partial_today),  0);
  const fullFuture   = (rows || []).reduce((a,b) => a + safeNum(b.full_future),    0);
  const partialFut   = (rows || []).reduce((a,b) => a + safeNum(b.partial_future), 0);
  const notWilling   = (rows || []).reduce((a,b) => a + safeNum(b.not_willing),    0);
  return {
    total, fullToday, partialToday, fullFuture, partialFut, notWilling,
    fullPct:       total > 0 ? (fullToday + fullFuture)    / total * 100 : null,
    partialPct:    total > 0 ? (partialToday + partialFut) / total * 100 : null,
    notWillingPct: total > 0 ? notWilling                  / total * 100 : null,
    todayPct:      total > 0 ? (fullToday + partialToday)  / total * 100 : null,
    futurePct:     total > 0 ? (fullFuture + partialFut)   / total * 100 : null,
  };
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 14px", minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
    }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none",
                border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition:  "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Semi-circle gauge (with optional delta) ────────────────────────
function GaugeCard({ label, value, pct = 0, color = BLUE, delta }) {
  const clamp = Math.min(100, Math.max(0, safeNum(pct)));
  const r     = 48;
  const cx    = 64;
  const cy    = 60;
  const circ  = Math.PI * r;
  const dash  = (clamp / 100) * circ;
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "10px 14px",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.05em", mb: 0.5, alignSelf: "flex-start",
      }}>
        {label}
      </Typography>
      <svg width={128} height={68} style={{ overflow: "visible" }}>
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke="#e8e8e8" strokeWidth={10} strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={color} strokeWidth={10} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
        />
        <text x={cx} y={cy - 4} textAnchor="middle"
          style={{ fontSize: 18, fontWeight: 500, fill: color }}>
          {value}
        </text>
      </svg>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function SentimentDashboard({ channel }) {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [polarity,     setPolarity]     = useState([]);
  const [emotion,      setEmotion]      = useState([]);
  const [score,        setScore]        = useState([]);
  const [willing,      setWilling]      = useState([]);
  // Prev-period datasets — KPI deltas only.
  const [prevPolarity, setPrevPolarity] = useState([]);
  const [prevEmotion,  setPrevEmotion]  = useState([]);
  const [prevScore,    setPrevScore]    = useState([]);
  const [prevWilling,  setPrevWilling]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [rangePreset, campaign, gran, channel]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [params.startDate, params.endDate, campaign, gran, channel]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [p, e, s, w, pp, pe, ps, pw] = await Promise.all([
        getPolarityData(params),
        getEmotionData(params),
        getScoreData(params),
        getWillingData(params),
        // Prev period: failures non-fatal — KPIs just won't show deltas.
        getPolarityData(prevParams).catch(() => []),
        getEmotionData(prevParams).catch(() => []),
        getScoreData(prevParams).catch(() => []),
        getWillingData(prevParams).catch(() => []),
      ]);
      setPolarity(p     || []);
      setEmotion(e      || []);
      setScore(s        || []);
      setWilling(w      || []);
      setPrevPolarity(pp || []);
      setPrevEmotion(pe  || []);
      setPrevScore(ps    || []);
      setPrevWilling(pw  || []);
    } catch (err) {
      console.error("Sentiment load error:", err?.response?.data, err?.message);
      setError(`Failed to load sentiment data: ${err?.response?.data?.error || err?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Polarity KPIs (with prev-period deltas) ────────────────────
  // Positive metrics good-when-up; negative metrics good-when-down.
  // Neutral defaults to good-when-up — neither direction is clearly favourable.
  const polKpis = useMemo(() => {
    const cur  = aggregatePolarity(polarity);
    const prev = aggregatePolarity(prevPolarity);
    return {
      ...cur,
      totalDelta:      buildDelta(cur.total,      prev.total,      { goodWhenUp: true  }),
      veryPosPctDelta: buildDelta(cur.veryPosPct, prev.veryPosPct, { goodWhenUp: true,  fmtVal: pctFmt }),
      posPctDelta:     buildDelta(cur.posPct,     prev.posPct,     { goodWhenUp: true,  fmtVal: pctFmt }),
      neuPctDelta:     buildDelta(cur.neuPct,     prev.neuPct,     { goodWhenUp: true,  fmtVal: pctFmt }),
      negPctDelta:     buildDelta(cur.negPct,     prev.negPct,     { goodWhenUp: false, fmtVal: pctFmt }),
      veryNegPctDelta: buildDelta(cur.veryNegPct, prev.veryNegPct, { goodWhenUp: false, fmtVal: pctFmt }),
    };
  }, [polarity, prevPolarity]);

  // ── Score KPIs ─────────────────────────────────────────────────
  // Avg score / score 5 good-when-up; score 1 good-when-down.
  const scoreKpis = useMemo(() => {
    const cur  = aggregateScore(score);
    const prev = aggregateScore(prevScore);
    return {
      ...cur,
      totalDelta:    buildDelta(cur.total,    prev.total,    { goodWhenUp: true  }),
      avgScoreDelta: buildDelta(cur.avgScore, prev.avgScore, { goodWhenUp: true,  fmtVal: score2dp }),
      score5Delta:   buildDelta(cur.score5,   prev.score5,   { goodWhenUp: true  }),
      score1Delta:   buildDelta(cur.score1,   prev.score1,   { goodWhenUp: false }),
    };
  }, [score, prevScore]);

  // ── Willing KPIs ───────────────────────────────────────────────
  // Any willingness > none, so full/partial/today/future all good-when-up;
  // not-willing good-when-down.
  const wilKpis = useMemo(() => {
    const cur  = aggregateWilling(willing);
    const prev = aggregateWilling(prevWilling);
    return {
      ...cur,
      totalDelta:         buildDelta(cur.total,         prev.total,         { goodWhenUp: true  }),
      futurePctDelta:     buildDelta(cur.futurePct,     prev.futurePct,     { goodWhenUp: true,  fmtVal: pctFmt }),
      partialPctDelta:    buildDelta(cur.partialPct,    prev.partialPct,    { goodWhenUp: true,  fmtVal: pctFmt }),
      notWillingPctDelta: buildDelta(cur.notWillingPct, prev.notWillingPct, { goodWhenUp: false, fmtVal: pctFmt }),
      todayPctDelta:      buildDelta(cur.todayPct,      prev.todayPct,      { goodWhenUp: true,  fmtVal: pctFmt }),
      fullPctDelta:       buildDelta(cur.fullPct,       prev.fullPct,       { goodWhenUp: true,  fmtVal: pctFmt }),
    };
  }, [willing, prevWilling]);

  // ── Emotion aggregates ─────────────────────────────────────────
  // Top emotions list is built from current period; deltas look up the
  // matching emotion in the prev period (same emotion, period-over-period).
  const emotionTotals = useMemo(() => {
    const curMap = {};
    emotion.forEach(r => { curMap[r.emotion] = (curMap[r.emotion] || 0) + safeNum(r.emotion_count); });
    const prevMap = {};
    prevEmotion.forEach(r => { prevMap[r.emotion] = (prevMap[r.emotion] || 0) + safeNum(r.emotion_count); });
    const curTotal = Object.values(curMap).reduce((a,b) => a + b, 0);
    return Object.entries(curMap)
      .sort((a,b) => b[1] - a[1])
      .map(([name, value]) => ({
        name, value,
        pct:   curTotal > 0 ? value / curTotal * 100 : 0,
        delta: buildDelta(value, prevMap[name] || 0, { goodWhenUp: true }),
      }));
  }, [emotion, prevEmotion]);

  // ── Chart data ─────────────────────────────────────────────────
  const polarityChartData = useMemo(() =>
    polarity.map(r => ({
      date:         fmtDate(r.summary_date),
      veryPositive: safeNum(r.very_positive),
      positive:     safeNum(r.positive),
      neutral:      safeNum(r.neutral),
      negative:     safeNum(r.negative),
      veryNegative: safeNum(r.very_negative),
      positivePct:  safeNum(r.positive_pct),
      neutralPct:   safeNum(r.neutral_pct),
      negativePct:  safeNum(r.negative_pct),
    }))
  , [polarity]);

  const scoreChartData = useMemo(() =>
    score.map(r => ({
      date:     fmtDate(r.summary_date),
      score1:   safeNum(r.score_1),
      score2:   safeNum(r.score_2),
      score3:   safeNum(r.score_3),
      score4:   safeNum(r.score_4),
      score5:   safeNum(r.score_5),
      avgScore: safeNum(r.avg_score),
    }))
  , [score]);

  const willingChartData = useMemo(() =>
    willing.map(r => ({
      date:          fmtDate(r.summary_date),
      fullToday:     safeNum(r.full_today),
      partialToday:  safeNum(r.partial_today),
      fullFuture:    safeNum(r.full_future),
      partialFuture: safeNum(r.partial_future),
      notWilling:    safeNum(r.not_willing),
      fullPct:       safeNum(r.full_pct),
      partialPct:    safeNum(r.partial_pct),
      notWillingPct: safeNum(r.not_willing_pct),
    }))
  , [willing]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Campaign
          </Typography>
          <Select
            value={campaign}
            onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading sentiment data…
        </Box>
      )}

      {!loading && (
        <>
          {/* ── POLARITY ──────────────────────────────────────── */}
          <SectionTitle>Polarity</SectionTitle>

          {/* KPI row — totals + positive sentiments + neutral */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <KpiCard label="Total records"   value={polKpis.total}                    color={BLUE}  delta={polKpis.totalDelta}      />
            <KpiCard label="Very positive %" value={`${fmt(polKpis.veryPosPct, 2)}%`} color={GREEN} delta={polKpis.veryPosPctDelta} />
            <KpiCard label="Positive %"      value={`${fmt(polKpis.posPct,     2)}%`} color={BLUE}  delta={polKpis.posPctDelta}     />
            <KpiCard label="Neutral %"       value={`${fmt(polKpis.neuPct,     2)}%`} color={AMBER} delta={polKpis.neuPctDelta}     />
          </Box>

          {/* Chart + negative gauges on the right */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 1.5, mb: 1.5 }}>
            <ChartCard title="Polarity over time" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={polarityChartData}>
                  <defs>
                    {["veryPositive","positive","neutral","negative","veryNegative"].map((k,i) => (
                      <linearGradient key={k} id={`grad_${k}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={Object.values(POLARITY_COLORS)[i]} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={Object.values(POLARITY_COLORS)[i]} stopOpacity={0}   />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="veryPositive" name="Very positive"
                    stroke={POLARITY_COLORS["Very Positive"]} fill="url(#grad_veryPositive)" strokeWidth={2} />
                  <Area type="monotone" dataKey="positive" name="Positive"
                    stroke={POLARITY_COLORS["Positive"]} fill="url(#grad_positive)" strokeWidth={2} />
                  <Area type="monotone" dataKey="neutral" name="Neutral"
                    stroke={POLARITY_COLORS["Neutral"]} fill="url(#grad_neutral)" strokeWidth={2} />
                  <Area type="monotone" dataKey="negative" name="Negative"
                    stroke={POLARITY_COLORS["Negative"]} fill="url(#grad_negative)" strokeWidth={2} />
                  <Area type="monotone" dataKey="veryNegative" name="Very negative"
                    stroke={POLARITY_COLORS["Very Negative"]} fill="url(#grad_veryNegative)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Negative gauges — actionable at a glance */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <GaugeCard
                label="Negative %"
                value={`${fmt(polKpis.negPct, 2)}%`}
                pct={polKpis.negPct}
                color={PINK}
                delta={polKpis.negPctDelta}
              />
              <GaugeCard
                label="Very negative %"
                value={`${fmt(polKpis.veryNegPct, 2)}%`}
                pct={polKpis.veryNegPct}
                color={RED}
                delta={polKpis.veryNegPctDelta}
              />
            </Box>
          </Box>

          {/* ── EMOTIONS ──────────────────────────────────────── */}
          <SectionTitle>Emotions</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            {emotionTotals.slice(0, 4).map((e, i) => (
              <KpiCard key={e.name} label={e.name} value={e.value}
                color={EMOTION_COLORS[i % EMOTION_COLORS.length]}
                delta={e.delta} />
            ))}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title="Emotion distribution" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={emotionTotals} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis type="number" tick={axisStyle} />
                  <YAxis type="category" dataKey="name"
                    tick={{ fontSize: 10, fill: "#888" }} width={110} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v, n, p) => [`${v} (${fmt(p.payload.pct, 1)}%)`, "Count"]}
                  />
                  <Bar dataKey="value" name="Count" radius={[0,3,3,0]}>
                    {emotionTotals.map((_, i) => (
                      <Cell key={i} fill={EMOTION_COLORS[i % EMOTION_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Emotion breakdown" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={emotionTotals} cx="50%" cy="50%"
                    innerRadius="45%" outerRadius="70%"
                    paddingAngle={2} dataKey="value" nameKey="name">
                    {emotionTotals.map((_, i) => (
                      <Cell key={i} fill={EMOTION_COLORS[i % EMOTION_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          {/* ── SENTIMENT SCORE ───────────────────────────────── */}
          <SectionTitle>Sentiment score</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <KpiCard label="Total records"  value={scoreKpis.total}            color={BLUE}  delta={scoreKpis.totalDelta}    />
            <KpiCard label="Avg score"      value={fmt(scoreKpis.avgScore, 2)} color={AMBER} delta={scoreKpis.avgScoreDelta} />
            <KpiCard label="Score 5 (best)" value={scoreKpis.score5}           color={GREEN} delta={scoreKpis.score5Delta}   />
            <KpiCard label="Score 1 (worst)" value={scoreKpis.score1}          color={PINK}  delta={scoreKpis.score1Delta}   />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title="Score distribution over time" height={200}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scoreChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                  {["score1","score2","score3","score4","score5"].map((k, i) => (
                    <Bar key={k} dataKey={k} name={`Score ${i + 1}`}
                      stackId="a" fill={SCORE_COLORS[i]}
                      radius={i === 4 ? [3,3,0,0] : [0,0,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Avg score trend" height={200}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={scoreChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} domain={[0, 5]} />
                  <Tooltip contentStyle={tooltipStyle}
                    formatter={v => [fmt(v, 2), "Avg score"]} />
                  <Area type="monotone" dataKey="avgScore" name="Avg score"
                    stroke={AMBER} fill="rgba(186,117,23,0.1)"
                    strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          {/* ── WILLINGNESS TO PAY ────────────────────────────── */}
          <SectionTitle>Willingness to pay</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <KpiCard label="Total records"    value={wilKpis.total}                       color={BLUE}  delta={wilKpis.totalDelta}         />
            <KpiCard label="Full future %"    value={`${fmt(wilKpis.futurePct,     2)}%`} color={AMBER} delta={wilKpis.futurePctDelta}     />
            <KpiCard label="Partial future %" value={`${fmt(wilKpis.partialPct,    2)}%`} color={AMBER} delta={wilKpis.partialPctDelta}    />
            <KpiCard label="Not willing %"    value={`${fmt(wilKpis.notWillingPct, 2)}%`} color={PINK}  delta={wilKpis.notWillingPctDelta} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 1.5, mb: 1.5 }}>
            <ChartCard title="Willingness over time" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={willingChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis yAxisId="left"  tick={axisStyle} />
                  <YAxis yAxisId="right" orientation="right"
                    tick={{ ...axisStyle, fill: PINK }}
                    tickFormatter={v => `${v}%`} domain={[0, 100]} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="fullToday"     name="Full today"
                    stackId="a" fill={WILLING_COLORS["Full today"]}     radius={[0,0,0,0]} />
                  <Bar yAxisId="left" dataKey="partialToday"  name="Partial today"
                    stackId="a" fill={WILLING_COLORS["Partial today"]}  radius={[0,0,0,0]} />
                  <Bar yAxisId="left" dataKey="fullFuture"    name="Full future"
                    stackId="a" fill={WILLING_COLORS["Full future"]}    radius={[0,0,0,0]} />
                  <Bar yAxisId="left" dataKey="partialFuture" name="Partial future"
                    stackId="a" fill={WILLING_COLORS["Partial future"]} radius={[0,0,0,0]} />
                  <Bar yAxisId="left" dataKey="notWilling"    name="Not willing"
                    stackId="a" fill={WILLING_COLORS["Not Willing"]}    radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <GaugeCard
                label="Partial today %"
                value={`${fmt(wilKpis.todayPct, 2)}%`}
                pct={wilKpis.todayPct}
                color={BLUE}
                delta={wilKpis.todayPctDelta}
              />
              <GaugeCard
                label="Full today %"
                value={`${fmt(wilKpis.fullPct, 2)}%`}
                pct={wilKpis.fullPct}
                color={GREEN}
                delta={wilKpis.fullPctDelta}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}